import { useCallback, useEffect, useMemo, useRef, memo } from "react";
import { ViewRegion } from "utils/metrics";
import { Player } from "./Player";
import { IGif, INicheMap, IUser } from "services/types";
import { useLikeActions } from "hooks/useLikeActions";
import { usePlayerActions } from "./hooks/usePlayerActions";
import { useVisitorInfo } from "hooks";
import { useSelector } from "react-redux";
import { IAppState } from "store/app-state";
import { onShare, onMenu as onMenuEvent } from "utils/busHandlers";
import { MenuShareEvent } from "utils/bus";
import { useExperiments } from "hooks/useExperiments";
import { usePlayerObserver } from "./hooks/usePlayerObserver";
import { usePlayerMetrics } from "./hooks/usePlayerMetrics";
import { useDismissScrollMore } from "hooks/useDismissScrollMore";
import { useFollow } from "./hooks/useFollow";
import { useBoostedHistory } from "./hooks/useBoostedHistory";

interface IPlayerContainerProps {
  gif: IGif;
  user: IUser | undefined;
  fullscreen: boolean;
  embedded?: boolean;
  hideGif: (id: string) => void;
  toggleFullScreen: (gif: IGif) => void;
  niches?: INicheMap;
  rel?: string;
  position: number;
  source: ViewRegion;
  sourceId: string | null;
}

export const PlayerContainer: React.FC<IPlayerContainerProps> = memo((props) => {
  const { sourceId, gif, fullscreen, toggleFullScreen, hideGif, source, position, niches } = props;
  const playerWrapRef = useRef<HTMLDivElement>(null);
  const { gifQuality, gifSound } = useSelector((state: IAppState) => state.application);
  const currentUser = useVisitorInfo();
  const { isVisible, isActive } = usePlayerObserver({ playerWrapRef, position, fullscreen });
  const experiments = useExperiments();
  const { isDismissScrollMore, dismissScrollMore } = useDismissScrollMore(currentUser);
  const isRotate = useMemo(() => experiments.rotate && fullscreen && gif.width > gif.height, [experiments, fullscreen, gif]);
  const isHLS = useMemo(() => !!gif.hls && experiments.hls, [gif, experiments]);

  useBoostedHistory({ isVisible, gif });

  const { followingIds, onFollow } = useFollow({
    context: sourceId,
    currentUser,
    username: gif.userName
  });

  const {
    toggleQuality,
    toggleSound,
    handleToggleFullScreen
  } = usePlayerActions({
    toggleFullScreen,
    gif,
    ref: playerWrapRef
  });

  const { liked, likesCount, handleLike } = useLikeActions({
    gifId: gif.id,
    source: sourceId,
    initialLikesCount: gif.likes,
    ref: playerWrapRef,
    currentUser
  });

  const onMenu = useCallback((options: MenuShareEvent) => {
    onMenuEvent({
      sourceId,
      fullscreen,
      hideGif,
      ...options
    });
  }, [fullscreen, hideGif, sourceId]);

  const {
    viewMetricQuery,
    onViewMetric,
    onProfileMetric,
    onVideoLoadMetric
  } = usePlayerMetrics({ gif, source, position, sourceId, isHLS });

  useEffect(() => {
    if (isVisible && gif.type !== 1) {
      onViewMetric();
    }
  }, [gif.type, isVisible, onViewMetric]);

  const gifNiches = useMemo(() => {
    if (!gif.niches || !niches) {
      return [];
    }

    return gif.niches.map(id => niches[id]).filter(Boolean);
  }, [gif.niches, niches]);

  return (
    <Player
      currentUser={currentUser}
      gif={gif}
      user={props.user}
      niches={gifNiches}
      rel={props.rel}
      position={props.position}
      source={props.source}
      sourceId={props.sourceId}
      playerWrapRef={playerWrapRef}
      gifQuality={gifQuality}
      gifSound={gifSound}
      fullscreen={fullscreen}
      liked={liked}
      likesCount={likesCount}
      isRotate={isRotate}
      isHLS={isHLS}
      isVisible={isVisible}
      isActive={isActive}
      followingIds={followingIds}
      onFollow={onFollow}
      isDismissScrollMore={isDismissScrollMore}
      onMenu={onMenu}
      onShare={onShare}
      onLike={handleLike}
      onFullScreen={handleToggleFullScreen}
      onSound={toggleSound}
      onQuality={toggleQuality}
      onViewMetric={onViewMetric}
      viewMetricQuery={viewMetricQuery}
      onProfileMetric={onProfileMetric}
      onVideoLoadMetric={onVideoLoadMetric}
      onDismissScrollMore={dismissScrollMore}
    />
  );
});
